/**
 * El dictamen técnico — EL papel del servicio de la Revisión.
 *
 * El paso 5 del protocolo promete: «sale de su casa con el documento en la mano,
 * la referencia normativa aplicable y el costo cerrado de la corrección». Este
 * componente cumple esa promesa: toma una intervención SELLADA por protocolo y
 * la imprime como dictamen — las mediciones del cliente, con su nombre, en papel
 * membretado. Es el documento que un electricista que cotiza por teléfono no
 * puede producir.
 *
 * Se genera desde la bitácora (botón «Dictamen» en la intervención) y usa el
 * mismo sistema de impresión que los cinco documentos de la cotización (#doc).
 * Lo que se imprime es lo que se midió: este componente NO edita nada — un
 * dictamen se corrige repitiendo la medición, no retocando el papel.
 */
import { MARCA } from '../marca.js'
import { Encabezado } from './Documentos.jsx'
import { BarraSimple, FotosRevision } from './Encargo.jsx'
import { TIPOS_INTERVENCION } from './Bitacora.jsx'

const fechaCorta = iso => (iso || '').slice(0, 10)

export default function VistaDictamen({ intervencion: iv, casa, cliente, cotizaciones, garantias, atras }) {
  const cotOrigen = cotizaciones.find(c => c.id === iv.cotizacionId)
  const garantia = (garantias || []).find(g => g.id === iv.garantiaId)
  const malos = (iv.respuestas || []).filter(r => r.tipo !== 'medicion' && r.ok === false)

  return (
    <div className="app">
      <BarraSimple titulo="Dictamen técnico" migaja={`${casa.alias} · ${cliente?.nombre || ''}`}
        atras={atras}
        acciones={<button className="ib pri noprint" onClick={() => window.print()}>Generar PDF</button>} />
      <main>
        <div id="doc">
          <Encabezado titulo="Dictamen técnico"
            cot={{ fecha: fechaCorta(iv.fecha), nombre: casa.alias }} cliente={cliente} />

          <div className="card">
            <div className="ln"><span>Propiedad</span><b>{casa.alias}{casa.direccion ? ` · ${casa.direccion}` : ''}</b></div>
            <div className="ln"><span>Tipo de visita</span><b>{TIPOS_INTERVENCION[iv.tipo] || iv.tipo}</b></div>
            <div className="ln"><span>Método seguido</span><b>{iv.protocoloNombre || 'Registro directo'}</b></div>
            <div className="ln"><span>Realizó</span><b>{iv.tecnico}</b></div>
          </div>

          {iv.respuestas?.length > 0 && (
            <>
              <div className="sec">Lo que se midió en su casa</div>
              <table className="d">
                <thead><tr><th>Punto verificado</th><th>Resultado</th><th>Observación</th></tr></thead>
                <tbody>
                  {iv.respuestas.map((r, k) => (
                    <tr key={k}>
                      <td>{r.paso}</td>
                      <td>{r.tipo === 'medicion'
                        ? `${r.valor}${r.unidad ? ` ${r.unidad}` : ''}`
                        : r.ok ? 'Cumple' : <b>NO CUMPLE</b>}</td>
                      <td>{r.nota || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {malos.length > 0 && (
                <div className="warn">
                  <b>{malos.length === 1 ? 'Un punto de su instalación no cumple' : `${malos.length} puntos de su instalación no cumplen`}:</b>{' '}
                  {malos.map(r => r.paso).join(' · ')}. El detalle y la corrección propuesta
                  están en el diagnóstico de abajo.
                </div>
              )}
            </>
          )}

          <div className="sec">Diagnóstico y propuesta</div>
          <div className="card">
            <div style={{ whiteSpace: 'pre-wrap' }}>{iv.descripcion}</div>
            {cotOrigen && (
              <div className="s" style={{ marginTop: 8 }}>
                La corrección propuesta está cotizada con <b>precio cerrado</b> en la
                propuesta <b>{cotOrigen.nombre}</b> del {cotOrigen.fecha}.
              </div>
            )}
            {garantia && (
              <div className="s" style={{ marginTop: 8 }}>
                Esta visita quedó amparada por la garantía vigente hasta el {garantia.fin}
                {garantia.facturaRef ? ` (factura ${garantia.facturaRef})` : ''}.
              </div>
            )}
          </div>

          {iv.fotos?.length > 0 && (
            <>
              <div className="sec">Evidencia fotográfica</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {iv.fotos.map((f, k) => (
                  <img key={k} src={f} alt={`Evidencia ${k + 1}`}
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 2 }} />
                ))}
              </div>
            </>
          )}

          <div className="s" style={{ marginTop: 18, lineHeight: 1.5 }}>
            Las mediciones y criterios de este dictamen se apoyan en la NOM-001-SEDE
            (Instalaciones Eléctricas) y se realizaron con instrumentos en sitio.
            Alcance del servicio: {MARCA.alcance}. Este documento describe el estado de la
            instalación el día de la visita.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 26 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ height: 90 }} />
              <div style={{ borderTop: '1px solid var(--negro)', paddingTop: 5, fontSize: 12 }}>
                {cliente?.nombre}<br /><b>Recibí el dictamen — nombre y firma</b>
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ height: 90 }} />
              <div style={{ borderTop: '1px solid var(--negro)', paddingTop: 5, fontSize: 12 }}>
                {iv.tecnico}<br /><b>Realizó la revisión</b>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
